import React, { useState, useEffect } from 'react';
import { Student } from '../../types';
import { X, Plus, Users, GraduationCap, Eye, EyeOff, Upload, Image as ImageIcon, AlertCircle, RefreshCw, CheckCircle } from 'lucide-react';
import { uploadStudentImage, deleteStudentImage } from '../../services';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import { generateEmailSuggestion } from '../../utils/student';
import { getClassesWithCampus, getBadges, Class as CampusClass, Badge } from '../../utils/management';

interface StudentFormProps {
    student?: Student;
    onSubmit: (student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
    loading?: boolean;
}

const StudentForm: React.FC<StudentFormProps> = ({ student, onSubmit, onCancel, loading = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [checkingStudentId, setCheckingStudentId] = useState(false);
    const [studentIdError, setStudentIdError] = useState('');
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const { showError, showSuccess } = useToast();

    // Dropdown list states
    const [dbClasses, setDbClasses] = useState<CampusClass[]>([]);
    const [dbBadges, setDbBadges] = useState<Badge[]>([]);
    const [campuses, setCampuses] = useState<any[]>([]);

    useEffect(() => {
        const loadDropdownData = async () => {
            try {
                const [classesData, badgesData, campusesData] = await Promise.all([
                    getClassesWithCampus(),
                    getBadges(),
                    import('../../utils/management').then(m => m.getCampuses())
                ]);
                setDbClasses(classesData);
                setDbBadges(badgesData);
                setCampuses(campusesData);
            } catch (error) {
                console.error('Error loading student form dropdowns:', error);
            }
        };
        loadDropdownData();
    }, []);

    // Track whether the email was manually edited by the user
    const [isEmailManuallyEdited, setIsEmailManuallyEdited] = useState(!!student);

    const [formData, setFormData] = useState({
        studentId: student?.studentId || '',
        firstName: student?.firstName || '',
        lastName: student?.lastName || '',
        email: student?.email || '',
        phone: student?.phone || '',
        dateOfBirth: student?.dateOfBirth ? (student.dateOfBirth instanceof Date ? student.dateOfBirth.toISOString().split('T')[0] : new Date(student.dateOfBirth).toISOString().split('T')[0]) : '',
        gender: student?.gender || 'male',
        password: '',
        confirmPassword: '',
        profileImage: student?.profileImage || '',
        selectedImageFile: null as File | null,
        address: {
            street: student?.address?.street || '',
            city: student?.address?.city || '',
            state: student?.address?.state || '',
            zipCode: student?.address?.zipCode || '',
            country: student?.address?.country || '',
        },
        parentInfo: {
            fatherName: student?.parentInfo?.fatherName || '',
            fatherPhone: student?.parentInfo?.fatherPhone || '',
            fatherEmail: student?.parentInfo?.fatherEmail || '',
            fatherOccupation: student?.parentInfo?.fatherOccupation || '',
            fatherWorkPlace: student?.parentInfo?.fatherWorkPlace || '',
            motherName: student?.parentInfo?.motherName || '',
            motherOccupation: student?.parentInfo?.motherOccupation || '',
            motherWorkPlace: student?.parentInfo?.motherWorkPlace || '',
        },
        academicInfo: {
            currentGrade: student?.academicInfo?.currentGrade || '',
            section: student?.academicInfo?.section || '',
            rollNumber: student?.academicInfo?.rollNumber || '',
            admissionDate: student?.academicInfo?.admissionDate ? (student.academicInfo.admissionDate instanceof Date ? student.academicInfo.admissionDate.toISOString().split('T')[0] : new Date(student.academicInfo.admissionDate).toISOString().split('T')[0]) : new Date().toISOString().split('T')[0],
            status: student?.academicInfo?.status || 'active',
            gradeHistory: student?.academicInfo?.gradeHistory || [],
            badge: (student?.academicInfo as any)?.badge || '',
        },
        emergencyContact: {
            name: student?.emergencyContact?.name || '',
            relationship: student?.emergencyContact?.relationship || '',
            phone: student?.emergencyContact?.phone || '',
            email: student?.emergencyContact?.email || '',
        },
    });

    const [newGrade, setNewGrade] = useState({
        grade: '',
        academicYear: '',
        section: '',
        rollNumber: '',
        status: 'completed' as 'completed' | 'failed' | 'promoted',
        marks: {
            totalMarks: 0,
            obtainedMarks: 0,
            percentage: 0,
        },
        remarks: '',
    });
    const [studentIdValid, setStudentIdValid] = useState(!!student);
    const [emailValid, setEmailValid] = useState(!!student);

    // Auto-generate suggested email when first name or last name changes, if user hasn't edited manually
    useEffect(() => {
        if (!isEmailManuallyEdited && formData.firstName.trim()) {
            const suggested = generateEmailSuggestion(formData.firstName, formData.lastName);
            setFormData(prev => ({
                ...prev,
                email: suggested
            }));
            setEmailError('');
            setEmailValid(false); // Trigger validation for new suggested email
        }
    }, [formData.firstName, formData.lastName, isEmailManuallyEdited]);

    // Auto-validate Student ID when user types (with debounce)
    useEffect(() => {
        if (formData.studentId.trim()) {
            const timer = setTimeout(() => {
                checkStudentIdUnique(formData.studentId);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.studentId]);

    // Auto-validate Email when user types (with debounce)
    useEffect(() => {
        if (formData.email.trim()) {
            const timer = setTimeout(() => {
                checkEmailUnique(formData.email);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [formData.email]);

    // Check if student ID already exists
    const checkStudentIdUnique = async (idToCheck: string) => {
        if (!idToCheck.trim()) {
            setStudentIdError('');
            return true;
        }

        // Skip check if editing and ID hasn't changed
        if (student && student.studentId === idToCheck.trim()) {
            setStudentIdError('');
            setStudentIdValid(true);
            return true;
        }

        try {
            setCheckingStudentId(true);
            const studentsRef = collection(db, 'students');
            const q = query(studentsRef, where('studentId', '==', idToCheck.trim()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setStudentIdError('This Student ID already exists. Please use a unique ID.');
                setStudentIdValid(false);
                return false;
            } else {
                setStudentIdError('');
                setStudentIdValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking student ID:', error);
            setStudentIdError('Error validating Student ID. Please try again.');
            return false;
        } finally {
            setCheckingStudentId(false);
        }
    };

    // Check if email already exists
    const checkEmailUnique = async (emailToCheck: string) => {
        if (!emailToCheck.trim()) {
            setEmailError('');
            return true;
        }

        // Skip check if editing and email hasn't changed
        if (student && student.email === emailToCheck.trim().toLowerCase()) {
            setEmailError('');
            setEmailValid(true);
            return true;
        }

        try {
            setCheckingEmail(true);
            const studentsRef = collection(db, 'students');
            const q = query(studentsRef, where('email', '==', emailToCheck.trim().toLowerCase()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setEmailError('This email is already registered. Please use a different email.');
                setEmailValid(false);
                return false;
            } else {
                setEmailError('');
                setEmailValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking email:', error);
            setEmailError('Error validating email. Please try again.');
            setEmailValid(false);
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    // Store selected image file
    const handleImageUpload = (file: File) => {
        setFormData(prev => ({
            ...prev,
            selectedImageFile: file
        }));

        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
        showSuccess('Success', 'Image selected successfully! Will be uploaded when you submit the form.');
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                showError('Invalid File', 'Please select an image file.');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                showError('File Too Large', 'Please select an image smaller than 5MB.');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);

            handleImageUpload(file);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === 'studentId') {
            setStudentIdError('');
            setStudentIdValid(false);
        }
        if (name === 'email') {
            setEmailError('');
            setEmailValid(false);
            
            // If user cleared the email field, enable auto-suggest again
            if (value.trim() === '') {
                setIsEmailManuallyEdited(false);
            } else {
                setIsEmailManuallyEdited(true);
            }
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...(prev[parent as keyof typeof prev] as any),
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const addGrade = () => {
        const trimmedGrade = newGrade.grade.trim();
        const trimmedAcademicYear = newGrade.academicYear.trim();

        if (!trimmedGrade || !trimmedAcademicYear) {
            showError('Required Fields', 'Please fill in grade and academic year.');
            return;
        }

        const gradeData = {
            ...newGrade,
            grade: trimmedGrade,
            academicYear: trimmedAcademicYear,
            marks: {
                ...newGrade.marks,
                percentage: newGrade.marks.totalMarks > 0
                    ? Math.round((newGrade.marks.obtainedMarks / newGrade.marks.totalMarks) * 100 * 100) / 100
                    : 0
            },
            addedDate: new Date(),
        };

        setFormData(prev => ({
            ...prev,
            academicInfo: {
                ...prev.academicInfo,
                gradeHistory: [...prev.academicInfo.gradeHistory, gradeData]
            }
        }));

        setNewGrade({
            grade: '',
            academicYear: '',
            section: '',
            rollNumber: '',
            status: 'completed',
            marks: {
                totalMarks: 0,
                obtainedMarks: 0,
                percentage: 0,
            },
            remarks: '',
        });

        setTimeout(() => {
            showSuccess('Success', 'Grade added successfully!');
        }, 100);
    };

    const removeGrade = (index: number) => {
        setFormData(prev => ({
            ...prev,
            academicInfo: {
                ...prev.academicInfo,
                gradeHistory: prev.academicInfo.gradeHistory.filter((_: any, i: number) => i !== index)
            }
        }));
    };

    const handleNewGradeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name.includes('marks.')) {
            const field = name.split('.')[1];
            setNewGrade(prev => ({
                ...prev,
                marks: {
                    ...prev.marks,
                    [field]: parseFloat(value) || 0
                }
            }));
        } else {
            setNewGrade(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Required Validations
        if (!formData.studentId.trim()) {
            showError('Student ID Required', 'Please enter a Student ID.');
            return;
        }

        const isStudentIdUnique = await checkStudentIdUnique(formData.studentId);
        if (!isStudentIdUnique) {
            showError('Duplicate Student ID', 'This Student ID already exists. Please use a unique ID.');
            return;
        }

        if (!formData.firstName.trim()) {
            showError('First Name Required', 'Please enter a First Name.');
            return;
        }

        if (!formData.email.trim()) {
            showError('Email Required', 'Please enter an email address.');
            return;
        }

        const isEmailUnique = await checkEmailUnique(formData.email);
        if (!isEmailUnique) {
            showError('Duplicate Email', 'This email is already registered. Please use a different email.');
            return;
        }

        if (!formData.gender) {
            showError('Gender Required', 'Please select a gender.');
            return;
        }

        if (!formData.dateOfBirth) {
            showError('Date of Birth Required', 'Please select a date of birth.');
            return;
        }

        if (!formData.academicInfo.currentGrade.trim()) {
            showError('Current Grade Required', 'Please enter a current grade.');
            return;
        }

        // Password validation for new students only
        if (!student) {
            if (!formData.password || formData.password.length < 6) {
                showError('Password Too Short', 'Password must be at least 6 characters long.');
                return;
            }
            if (formData.password !== formData.confirmPassword) {
                showError('Passwords Mismatch', 'Passwords do not match. Please check and try again.');
                return;
            }
        }

        const extractPublicId = (url: string): string | null => {
            const match = url.match(/\/v\d+\/(.+)\./);
            return match ? match[1] : null;
        };

        // Upload image if selected
        let profileImageUrl = formData.profileImage;
        if (formData.selectedImageFile) {
            setUploadingImage(true);
            try {
                if (student && student.profileImage) {
                    const oldPublicId = extractPublicId(student.profileImage);
                    if (oldPublicId) {
                        try {
                            await deleteStudentImage(oldPublicId);
                        } catch (deleteError) {
                            console.warn('Failed to delete old image:', deleteError);
                        }
                    }
                }

                const result = await uploadStudentImage(formData.selectedImageFile, formData.studentId);
                profileImageUrl = result.secure_url;
            } catch (error) {
                console.error('Error uploading image:', error);
                showError('Error', 'Failed to upload image. Please try again.');
                setUploadingImage(false);
                return;
            }
            setUploadingImage(false);
        }

        const studentData = {
            ...formData,
            dateOfBirth: new Date(formData.dateOfBirth),
            profileImage: profileImageUrl,
            academicInfo: {
                ...formData.academicInfo,
                admissionDate: new Date(formData.academicInfo.admissionDate),
            },
        };

        const { selectedImageFile, ...submittedData } = studentData;

        onSubmit(submittedData);
    };

    return (
        <Modal
            isOpen={true}
            onClose={onCancel}
            title={student ? 'Edit Student Details' : 'Add New Student'}
            size="lg"
            footer={
                <>
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="student-form"
                        size="sm"
                        loading={loading || uploadingImage || checkingStudentId || checkingEmail}
                        disabled={loading || uploadingImage || checkingStudentId || checkingEmail || !!studentIdError || !!emailError}
                    >
                        {loading ? 'Saving...' : student ? 'Update Student' : 'Add Student'}
                    </Button>
                </>
            }
        >
            {/* Loading Overlay */}
            {(loading || uploadingImage) && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        <div className="text-gray-700 font-medium text-xs">
                            {uploadingImage
                                ? 'Uploading image...'
                                : student ? 'Updating student details...' : 'Creating student account...'
                            }
                        </div>
                    </div>
                </div>
            )}

            <form 
                id="student-form" 
                onSubmit={handleSubmit} 
                className={`space-y-6 relative text-xs ${(loading || uploadingImage) ? 'pointer-events-none opacity-50' : ''}`}
            >
                {/* Basic Information */}
                <Card className="p-5 islamic-pattern">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center border-b pb-2">
                        <Users className="h-4.5 w-4.5 mr-2 text-emerald-600" />
                        Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Student ID <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="studentId"
                                    value={formData.studentId}
                                    onChange={handleInputChange}
                                    placeholder="Enter unique student ID"
                                    required
                                    disabled={checkingStudentId}
                                    className={`w-full px-3 py-2 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                                        studentIdError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {checkingStudentId && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!checkingStudentId && studentIdError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!checkingStudentId && !studentIdError && studentIdValid && formData.studentId.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {studentIdError && (
                                <p className="mt-1 text-red-600 flex items-center text-[10px]">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {studentIdError}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter first name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Last Name
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                placeholder="Enter last name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Email <span className="text-red-500">*</span>
                                {!isEmailManuallyEdited && formData.firstName.trim() && (
                                    <span className="text-[10px] text-emerald-600 ml-1.5 font-normal">(Auto-suggested)</span>
                                )}
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email address"
                                    required
                                    disabled={checkingEmail}
                                    autoComplete="off"
                                    className={`w-full px-3 py-2 pr-10 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
                                        emailError
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300 focus:ring-emerald-500 focus:border-transparent'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                />
                                {checkingEmail && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                    </div>
                                )}
                                {!checkingEmail && emailError && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <AlertCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                )}
                                {!checkingEmail && !emailError && emailValid && formData.email.trim() && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                    </div>
                                )}
                            </div>
                            {emailError && (
                                <p className="mt-1 text-red-600 flex items-center text-[10px]">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {emailError}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Phone
                            </label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="Enter phone number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Date of Birth <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Gender <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            >
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Profile Image Upload */}
                        <div className="md:col-span-3">
                            <label className="block text-gray-700 font-medium mb-1.5">Profile Image</label>
                            <div className="flex items-center space-x-4 bg-gray-50/50 p-3 rounded-xl border border-dashed border-gray-200">
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border">
                                        {imagePreview || formData.profileImage ? (
                                            <img
                                                src={imagePreview || formData.profileImage}
                                                alt="Profile Preview"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <ImageIcon className="h-6 w-6 text-gray-400" />
                                        )}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                        id="profileImage"
                                    />
                                    <label
                                        htmlFor="profileImage"
                                        className="cursor-pointer inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-all font-medium"
                                    >
                                        {uploadingImage ? (
                                            <>
                                                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-3.5 w-3.5 mr-1.5" />
                                                Upload Image
                                            </>
                                        )}
                                    </label>
                                    <p className="text-[10px] text-gray-400 mt-1">JPG, PNG or GIF (Max 5MB)</p>
                                </div>
                            </div>
                        </div>

                        {/* Password fields only for new student addition */}
                        {!student && (
                            <>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-1.5">
                                        Password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="Create a secure password"
                                            required={!student}
                                            autoComplete="new-password"
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-1.5">
                                        Confirm Password <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            placeholder="Confirm password"
                                            required={!student}
                                            autoComplete="new-password"
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Card>

                {/* Address Information (Optional) */}
                <Card className="p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 border-b pb-2">Address Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Street Address
                            </label>
                            <input
                                type="text"
                                name="address.street"
                                value={formData.address.street}
                                onChange={handleInputChange}
                                placeholder="Enter street address"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                City
                            </label>
                            <input
                                type="text"
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleInputChange}
                                placeholder="Enter city"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                State
                            </label>
                            <input
                                type="text"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleInputChange}
                                placeholder="Enter state"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                ZIP Code
                            </label>
                            <input
                                type="text"
                                name="address.zipCode"
                                value={formData.address.zipCode}
                                onChange={handleInputChange}
                                placeholder="Enter ZIP Code"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Country
                            </label>
                            <input
                                type="text"
                                name="address.country"
                                value={formData.address.country}
                                onChange={handleInputChange}
                                placeholder="Enter country"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                    </div>
                </Card>

                {/* Parent Information (Optional) */}
                <Card className="p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 border-b pb-2">Parent Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Father's Name
                            </label>
                            <input
                                type="text"
                                name="parentInfo.fatherName"
                                value={formData.parentInfo.fatherName}
                                onChange={handleInputChange}
                                placeholder="Enter father's name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Mother's Name
                            </label>
                            <input
                                type="text"
                                name="parentInfo.motherName"
                                value={formData.parentInfo.motherName}
                                onChange={handleInputChange}
                                placeholder="Enter mother's name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Father's Phone
                            </label>
                            <input
                                type="tel"
                                name="parentInfo.fatherPhone"
                                value={formData.parentInfo.fatherPhone}
                                onChange={handleInputChange}
                                placeholder="Enter father's phone number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Father's Email
                            </label>
                            <input
                                type="email"
                                name="parentInfo.fatherEmail"
                                value={formData.parentInfo.fatherEmail}
                                onChange={handleInputChange}
                                placeholder="Enter father's email"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Father's Occupation
                            </label>
                            <input
                                type="text"
                                name="parentInfo.fatherOccupation"
                                value={formData.parentInfo.fatherOccupation}
                                onChange={handleInputChange}
                                placeholder="Enter father's occupation"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Father's Work Place
                            </label>
                            <input
                                type="text"
                                name="parentInfo.fatherWorkPlace"
                                value={formData.parentInfo.fatherWorkPlace}
                                onChange={handleInputChange}
                                placeholder="Enter father's workplace"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Mother's Occupation
                            </label>
                            <input
                                type="text"
                                name="parentInfo.motherOccupation"
                                value={formData.parentInfo.motherOccupation}
                                onChange={handleInputChange}
                                placeholder="Enter mother's occupation"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Mother's Work Place
                            </label>
                            <input
                                type="text"
                                name="parentInfo.motherWorkPlace"
                                value={formData.parentInfo.motherWorkPlace}
                                onChange={handleInputChange}
                                placeholder="Enter mother's workplace"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                    </div>
                </Card>

                {/* Academic Information */}
                <Card className="p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center border-b pb-2">
                        <GraduationCap className="h-4.5 w-4.5 mr-2 text-emerald-600" />
                        Academic Information
                    </h3>

                    {/* Current Academic Details */}
                    <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Current Academic Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-gray-700 font-medium mb-1.5">
                                    Current Grade <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="classSelector"
                                    value={formData.academicInfo.currentGrade && formData.academicInfo.section ? `${formData.academicInfo.currentGrade}|${formData.academicInfo.section}` : ""}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val) {
                                            const [grade, section] = val.split('|');
                                            setFormData(prev => ({
                                                ...prev,
                                                academicInfo: {
                                                    ...prev.academicInfo,
                                                    currentGrade: grade,
                                                    section: section
                                                }
                                            }));
                                        } else {
                                            setFormData(prev => ({
                                                ...prev,
                                                academicInfo: {
                                                    ...prev.academicInfo,
                                                    currentGrade: '',
                                                    section: ''
                                                }
                                            }));
                                        }
                                    }}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white"
                                >
                                    <option value="">-- Select Class --</option>
                                    {dbClasses.map(c => {
                                        const campus = campuses.find(cam => cam.id === c.campusId);
                                        const campusName = campus ? `(${campus.name})` : '';
                                        return (
                                            <option key={c.id} value={`${c.grade}|${c.section}`}>
                                                Grade {c.grade} - Section {c.section} {campusName}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1.5">
                                    Section
                                </label>
                                <input
                                    type="text"
                                    name="academicInfo.section"
                                    value={formData.academicInfo.section}
                                    disabled
                                    placeholder="Auto-populated"
                                    className="w-full px-3 py-2 border border-gray-200 bg-slate-50 text-gray-700 font-semibold rounded-xl cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1.5">
                                    Roll Number
                                </label>
                                <input
                                    type="text"
                                    name="academicInfo.rollNumber"
                                    value={formData.academicInfo.rollNumber}
                                    onChange={handleInputChange}
                                    placeholder="Enter roll number"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1.5">
                                    Admission Date
                                </label>
                                <input
                                    type="date"
                                    name="academicInfo.admissionDate"
                                    value={formData.academicInfo.admissionDate}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1.5">
                                    Status
                                </label>
                                <select
                                    name="academicInfo.status"
                                    value={formData.academicInfo.status}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="graduated">Graduated</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-700 font-medium mb-1.5">
                                    Student Badge
                                </label>
                                <select
                                    name="academicInfo.badge"
                                    value={formData.academicInfo.badge}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setFormData(prev => ({
                                            ...prev,
                                            academicInfo: {
                                                ...prev.academicInfo,
                                                badge: val
                                            }
                                        }));
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all font-semibold text-gray-700 bg-white"
                                >
                                    <option value="">-- No Badge --</option>
                                    {dbBadges.map(b => {
                                        const campus = campuses.find(cam => cam.id === b.campusId);
                                        const campusName = campus ? `[${campus.name}]` : '';
                                        return (
                                            <option key={b.id} value={b.title}>
                                                {b.title} (Badge #{b.badgeNumber} - {b.year}) {campusName}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Grade History Management */}
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Academic Grade History</h4>
                        <p className="text-gray-500 text-[11px] mb-4">
                            Add completed grades, promotions, and examination results. This tracks student progress over time.
                        </p>

                        {/* Add New Grade Form */}
                        <div className="bg-gray-50/50 border border-gray-150 rounded-xl p-4 mb-4">
                            <h5 className="font-semibold text-gray-800 mb-3 text-xs">Add New Grade Record</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                <Input
                                    label="Grade"
                                    name="grade"
                                    value={newGrade.grade}
                                    onChange={handleNewGradeChange}
                                    placeholder="e.g., 9th, 10th"
                                    className="text-xs"
                                />
                                <Input
                                    label="Academic Year"
                                    name="academicYear"
                                    value={newGrade.academicYear}
                                    onChange={handleNewGradeChange}
                                    placeholder="e.g., 2023-2024"
                                    className="text-xs"
                                />
                                <Input
                                    label="Section"
                                    name="section"
                                    value={newGrade.section}
                                    onChange={handleNewGradeChange}
                                    placeholder="e.g., A, B"
                                    className="text-xs"
                                />
                                <Input
                                    label="Roll Number"
                                    name="rollNumber"
                                    value={newGrade.rollNumber}
                                    onChange={handleNewGradeChange}
                                    className="text-xs"
                                />
                                <Select
                                    label="Status"
                                    name="status"
                                    value={newGrade.status}
                                    onChange={handleNewGradeChange}
                                    options={[
                                        { value: 'completed', label: 'Completed' },
                                        { value: 'promoted', label: 'Promoted' },
                                        { value: 'failed', label: 'Failed' }
                                    ]}
                                />
                                <Input
                                    label="Total Marks"
                                    name="marks.totalMarks"
                                    type="number"
                                    value={newGrade.marks.totalMarks}
                                    onChange={handleNewGradeChange}
                                    className="text-xs"
                                />
                                <Input
                                    label="Obtained Marks"
                                    name="marks.obtainedMarks"
                                    type="number"
                                    value={newGrade.marks.obtainedMarks}
                                    onChange={handleNewGradeChange}
                                    className="text-xs"
                                />
                                <div className="flex items-end">
                                    <div className="w-full">
                                        <label className="block text-gray-700 font-medium mb-1.5">Percentage</label>
                                        <div className="px-3 py-2 border border-gray-300 rounded-xl bg-gray-100 text-gray-500 font-semibold text-xs">
                                            {newGrade.marks.totalMarks > 0
                                                ? `${Math.round((newGrade.marks.obtainedMarks / newGrade.marks.totalMarks) * 100 * 100) / 100}%`
                                                : '0%'
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-1.5">Remarks</label>
                                <textarea
                                    name="remarks"
                                    value={newGrade.remarks}
                                    onChange={handleNewGradeChange}
                                    placeholder="Any additional notes or remarks..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                    rows={2}
                                />
                            </div>
                            <Button
                                type="button"
                                onClick={addGrade}
                                className="flex items-center space-x-1.5"
                                size="sm"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                <span>Add Grade Record</span>
                            </Button>
                        </div>

                        {/* Grade History List */}
                        {formData.academicInfo.gradeHistory.length > 0 && (
                            <div>
                                <h5 className="font-semibold text-gray-800 mb-3 text-xs">Grade History</h5>
                                <div className="space-y-2">
                                    {formData.academicInfo.gradeHistory.map((grade: any, index: number) => (
                                        <div key={index} className="bg-white border border-gray-200 rounded-xl p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
                                                        <div>
                                                            <span className="text-gray-500 font-medium">Grade:</span>
                                                            <p className="text-gray-900 font-semibold">{grade.grade}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 font-medium">Academic Year:</span>
                                                            <p className="text-gray-900 font-semibold">{grade.academicYear}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 font-medium">Status:</span>
                                                            <div>
                                                                <span className={`inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full ${
                                                                    grade.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                                    grade.status === 'promoted' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {grade.status}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 font-medium">Percentage:</span>
                                                            <p className="text-gray-900 font-bold">{grade.marks.percentage}%</p>
                                                        </div>
                                                    </div>
                                                    {grade.remarks && (
                                                        <div className="mt-1.5 pt-1.5 border-t border-dashed border-gray-100">
                                                            <span className="text-gray-400 font-medium">Remarks: </span>
                                                            <span className="text-gray-600">{grade.remarks}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeGrade(index)}
                                                    className="ml-3 p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Emergency Contact (Optional) */}
                <Card className="p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Emergency Contact</h3>
                    <p className="text-gray-400 text-[10px] mb-4">Optional - Can be provided later</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Contact Name
                            </label>
                            <input
                                type="text"
                                name="emergencyContact.name"
                                value={formData.emergencyContact.name}
                                onChange={handleInputChange}
                                placeholder="Enter emergency contact's full name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Relationship
                            </label>
                            <input
                                type="text"
                                name="emergencyContact.relationship"
                                value={formData.emergencyContact.relationship}
                                onChange={handleInputChange}
                                placeholder="e.g., Father, Mother, Uncle"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Phone
                            </label>
                            <input
                                type="text"
                                name="emergencyContact.phone"
                                value={formData.emergencyContact.phone}
                                onChange={handleInputChange}
                                placeholder="Enter contact phone number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-medium mb-1.5">
                                Email
                            </label>
                            <input
                                type="email"
                                name="emergencyContact.email"
                                value={formData.emergencyContact.email}
                                onChange={handleInputChange}
                                placeholder="Enter contact email address"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                            />
                        </div>
                    </div>
                </Card>
            </form>
        </Modal>
    );
};

export default StudentForm;
